import { Injectable } from '@angular/core';
import { CultivationProductLinkApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation-product-link/cultivation-product-link-api';
import { NativeBridgeService } from '../webview/native-bridge.service';
import { PosthogService } from './posthog.service';

/**
 * Étiquette `utm_medium` des liens « Acheter les graines » (CR-07) : les fiches (plante,
 * graine, guide de culture) et le plan de saison, pour mesurer les ventes de chaque entrée.
 */
export type ShopUtmMedium = 'fiche' | 'plan_saison';

/**
 * Resolves the webshop URL for a cultivation (server-side, evolutive) and opens it in the
 * EXTERNAL browser. The resolution + URL format live in the backend so they can change
 * without an app release; the app just opens whatever URL the endpoint returns.
 */
@Injectable({ providedIn: 'root' })
export class ShopRedirectService {
  // SDK API is config-driven via the global SdkConfigService singleton, so a direct
  // instantiation works here (no Angular DI provider needed for a root service).
  private readonly cultivationProductLinkApi = new CultivationProductLinkApi();

  // Last-resort fallback ONLY if the resolve call itself is unreachable (network error):
  // the 99% path uses the backend-returned URL. Kept minimal so the button is never dead.
  private static readonly SHOP_HOME_FALLBACK = 'https://www.vegiwise.com';
  private static readonly DEFAULT_CAMPAIGN = 'acheter_graines';

  constructor(
    private nativeBridge: NativeBridgeService,
    private posthogService: PosthogService,
  ) {}

  /**
   * Resolve the webshop URL for a cultivation and open it in the external browser.
   * Never throws.
   */
  async buyForCultivation(
    cultivationUid: string,
    medium: ShopUtmMedium = 'fiche',
    campaign: string = ShopRedirectService.DEFAULT_CAMPAIGN,
  ): Promise<void> {
    if (!cultivationUid) {
      return;
    }
    // Mobile browsers only allow window.open during the SYNCHRONOUS part of the
    // user gesture: pre-open the tab now (before any await), fill the URL later.
    // Inside the Flutter WebView the native channel is used instead (no tab).
    const pendingTab = this.hasNativeChannel() ? null : window.open('', '_blank');
    let url = ShopRedirectService.SHOP_HOME_FALLBACK;
    let source = 'fallback';
    try {
      const res = await this.cultivationProductLinkApi.resolveShopUrl(cultivationUid);
      // Only open an ABSOLUTE http(s) URL — a relative URL would open the app itself in the tab.
      if (res?.url && /^https?:\/\//i.test(res.url)) {
        url = res.url;
        source = res.source ?? 'fallback';
      }
    } catch (e) {
      console.error('Failed to resolve shop URL, opening shop homepage', e);
    }
    this.posthogService.capture('shop_redirect', { cultivationUid, source });
    this.openExternal(ShopRedirectService.withUtm(url, medium, campaign), pendingTab);
  }

  /**
   * Open a webshop URL already resolved by the backend (plan de saison : one link per line).
   * Same safety as buyForCultivation: only an absolute http(s) URL, else the shop homepage.
   */
  openShopUrl(url: string | undefined, cultivationUid: string, medium: ShopUtmMedium, campaign: string): void {
    const safeUrl = url && /^https?:\/\//i.test(url) ? url : ShopRedirectService.SHOP_HOME_FALLBACK;
    this.posthogService.capture('shop_redirect', { cultivationUid, source: medium });
    this.openExternal(ShopRedirectService.withUtm(safeUrl, medium, campaign));
  }

  /** Ajoute `utm_source=app`, `utm_medium` et `utm_campaign` (CR-07), sans toucher au reste de l'URL. */
  static withUtm(url: string, medium: ShopUtmMedium, campaign: string): string {
    try {
      const tagged = new URL(url);
      tagged.searchParams.set('utm_source', 'app');
      tagged.searchParams.set('utm_medium', medium);
      tagged.searchParams.set('utm_campaign', campaign);
      return tagged.toString();
    } catch {
      return url;
    }
  }

  private hasNativeChannel(): boolean {
    return this.nativeBridge.canOpenExternal();
  }

  /**
   * Open a URL in the EXTERNAL browser. Inside the Flutter WebView, *.vegiwise.com is
   * whitelisted, so a plain navigation would load the shop INSIDE the app — we MUST route
   * through the native channel. Cascade: dedicated channel → OAuth channel (older apps)
   * → pre-opened tab (popup-blocker safe) → window.open as a last resort.
   */
  private openExternal(url: string, pendingTab: Window | null = null): void {
    if (this.nativeBridge.openExternal(url)) {
      return;
    } else if (pendingTab && !pendingTab.closed) {
      pendingTab.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  }
}
